import { z } from 'zod';
import { objectId } from './common';
import { getReportDef, registerReportDefs, reportColumn } from './reports';
import type { ReportColumn, ReportDef } from './reports';
import type { UserRef } from './users';

/**
 * Customize Reports (reference PC429, "Customizable Reports"), Saved report settings (PC520,
 * "Saved Parameters") and Scheduled reports (PC527, "Schedule Task").
 *
 * A customizable report is a builder: search parameters, then column groups with one checkbox
 * per output column, and a CSV file as the only output type. The reference lists 35 builders
 * in four groups; all 35 were read on 2026-10-14 (product inventory 13.12). Here a builder is
 * built when this application holds its data, with the reference's group titles and the
 * columns it has data for. A builder named after a report that exists here offers that
 * report's columns and filters.
 */

export const CUSTOM_REPORT_GROUPS = [
  ['subscriber', 'Subscriber Reports'],
  ['lists', 'Subscriber Lists'],
  ['inventory', 'Inventory Reports'],
  ['billing', 'Billing Reports'],
] as const;
export type CustomReportGroup = (typeof CUSTOM_REPORT_GROUPS)[number][0];

export interface CustomReport {
  key: string;
  group: CustomReportGroup;
  name: string;
  /** The report definition that supplies the filters, the columns and the rows. */
  source: string;
  /** Column groups of the builder; absent means one group, "Output Parameter", with every column. */
  sections?: ReadonlyArray<{ title: string; keys: readonly string[] }>;
  /** A builder added after a deployment: offered only while this release feature is on. */
  feature?: string;
}

const c = reportColumn;

/** "Output Parameter" of Subscriber Information, in the reference's order. */
const SUBSCRIBER_COLUMNS: ReportColumn[] = [
  c('accountType', 'Account Type'),
  c('enrollmentId', 'Enrollment ID'),
  c('accountNo', 'Account Number'),
  c('address1', 'Address 1'),
  c('address2', 'Address 2'),
  c('billingAddress1', 'Billing Address 1'),
  c('billingAddress2', 'Billing Address 2'),
  c('billingCity', 'Billing City'),
  c('billingState', 'Billing State'),
  c('billingZip', 'Billing Zipcode'),
  c('city', 'City'),
  c('state', 'State'),
  c('zip', 'ZIP'),
  c('contactPhone', 'Contact Phone'),
  c('customerId', 'Customer ID'),
  c('status', 'Subscriber Status'),
  c('activatedAt', 'Application Activation Date', 'datetime'),
  c('email', 'Email Address'),
  c('firstName', 'First Name'),
  c('middleName', 'Middle Name'),
  c('lastName', 'Last Name'),
  c('mdn', 'Main MDN'),
  c('planName', 'Plan'),
  c('carrier', 'Carrier'),
  c('sim', 'SIM'),
  c('createdBy', 'Created By'),
  c('disconnectedAt', 'Disconnection Date', 'datetime'),
  c('suspendedAt', 'Suspension Date', 'datetime', 'reports-extras-2'),
  c('source', 'Source'),
  c('agentName', 'Agent Name'),
];

/** "Agent Information" (AG): the user who sold the line. */
const AGENT_INFO_COLUMNS: ReportColumn[] = [
  c('agAddress1', 'Agent Address 1'),
  c('agAddress2', 'Agent Address 2'),
  c('agAccountName', 'Agent Account Name'),
  c('agClass', 'Agent Class'),
  c('agStatus', 'Agent Status'),
  c('agLoginId', 'Agent Login ID'),
  c('agCity', 'Agent City'),
  c('agCommissionLevel', 'Agent Commission Level'),
  c('agPhone', 'Agent Contact Phone'),
  c('agCreatedAt', 'Agent Created Date', 'datetime'),
  c('agEmail', 'Agent Email'),
  c('agFirstName', 'Agent First Name'),
  c('agLastName', 'Agent Last Name'),
  c('agState', 'Agent State'),
  c('agZip', 'Agent Zip Code'),
];

/** "MDN Information" less "Date Until Disconnected" and "Date Until Suspended" (not kept). */
const MDN_COLUMNS: ReportColumn[] = [
  c('mdnActivatedAt', 'Activation Date', 'datetime'),
  c('mdnCreatedAt', 'Date Created', 'datetime'),
  c('mdnDisconnectedAt', 'Disconnect Date', 'datetime'),
];

/** "Other Information" of Subscriber Status Report. */
const OTHER_COLUMNS: ReportColumn[] = [
  c('disconnectReason', 'Disconnected Reasons'),
  c('carrierPlanId', 'Carrier Plan ID'),
  c('masterName', 'Master Name'),
  c('distributorName', 'Distributor Name'),
  c('retailerName', 'Retailer Name'),
  c('chainAgentName', 'Agent Name'),
  c('lastNote', 'Last Note'),
];

/** "Inventory Information" of the same builder: the SIM the subscriber holds. */
const SUBSCRIBER_INVENTORY_COLUMNS: ReportColumn[] = [
  c('invSim', 'SIM'),
  c('invModel', 'Model'),
  c('invCarrier', 'Carrier'),
  c('invUploadedBy', 'Uploaded By'),
  c('invStatus', 'Inventory Status'),
  c('invMasterAgent', 'Master Agent'),
  c('invDistributor', 'Distributor'),
  c('invRetailer', 'Retailer'),
  c('invAgent', 'Agent'),
  c('invEnrollId', 'Enroll ID'),
  c('invDeviceId', 'Device ID'),
];

/** The rows behind the Subscriber Information builder. It has no screen of its own. */
const SUBSCRIBER_ALL = [
  ...SUBSCRIBER_COLUMNS,
  ...SUBSCRIBER_INVENTORY_COLUMNS,
  ...AGENT_INFO_COLUMNS,
  ...MDN_COLUMNS,
  ...OTHER_COLUMNS,
];
export const SUBSCRIBER_INFORMATION_DEF: ReportDef = {
  key: 'subscriber-information',
  group: 'screen',
  name: 'Subscriber Information',
  heading: 'Subscriber Information',
  code: 'PC429',
  filters: [{ type: 'dateRange', key: 'date', label: 'Date Range' }],
  columns: SUBSCRIBER_ALL,
};

/** The same rows, searched by status (Subscriber Status Report). */
const SUBSCRIBER_STATUS_DEF: ReportDef = {
  ...SUBSCRIBER_INFORMATION_DEF,
  key: 'subscriber-status',
  name: 'Subscriber Status Report',
  heading: 'Subscriber Status Report',
  filters: [
    {
      type: 'select',
      key: 'status',
      label: 'Status',
      options: ['Active', 'Dispatched', 'Inactive', 'Suspend'],
      emptyLabel: 'Select',
    },
  ],
};

/** Subscriber columns that the payment, note and SIM builders show beside their own. */
const SUBSCRIBER_SHORT: ReportColumn[] = [
  c('customerId', 'Customer ID'),
  c('accountType', 'Account Type'),
  c('enrollmentId', 'Enrollment ID'),
  c('firstName', 'First Name'),
  c('lastName', 'Last Name'),
  c('mdn', 'Main MDN'),
  c('city', 'City'),
  c('state', 'State'),
  c('zip', 'ZIP'),
  c('email', 'Email Address'),
  c('status', 'Subscriber Status'),
];
const CHAIN_COLUMNS: ReportColumn[] = [
  c('masterAgent', 'Master Agent'),
  c('distributor', 'Distributor'),
  c('retailer', 'Retailer'),
  c('agentName', 'Agent Name'),
  c('orderBy', 'Order By'),
];

/** "Payment Information" of Subscriber Payments. */
const PAYMENT_INFO_COLUMNS: ReportColumn[] = [
  c('invoiceNo', 'Invoice Number'),
  c('subscriberId', 'Subscriber ID'),
  c('invoiceType', 'Invoices Type'),
  c('paymentCategory', 'Payment Category'),
  c('paymentDescription', 'Payment Description'),
  c('transactionDate', 'Transaction Date', 'datetime'),
  c('transactionType', 'Transaction Type'),
  c('totalAmount', 'Total Amount', 'money'),
  c('taxAmount', 'Tax Amount', 'money'),
  c('void', 'Void'),
  c('paySource', 'Source'),
  c('transactionId', 'Transaction ID'),
  c('invoiceCreateBy', 'Invoice Create By'),
];
/** "Invoice Information" of Billing Invoices (and of Invoices, less its 31 other columns). */
const INVOICE_INFO_COLUMNS: ReportColumn[] = [
  c('invoiceNo', 'Invoice Number'),
  c('subscriberId', 'Subscriber ID'),
  c('invoiceType', 'Invoices Type'),
  c('invoiceCreated', 'Invoice Created', 'datetime'),
  c('amount', 'Amount', 'money'),
  c('taxAmount', 'Tax Amount', 'money'),
  c('totalAmount', 'Total Amount', 'money'),
  c('planId', 'Plan ID'),
  c('transactionId', 'Transaction ID'),
  c('invoiceCreateBy', 'Invoice Create By'),
];
/** "Purchase Information" of Subscriber Purchases Detail. */
const PURCHASE_INFO_COLUMNS: ReportColumn[] = [
  c('transactionType', 'Payment Type'),
  c('paySource', 'Payment Source'),
  c('invoiceType', 'Purchase Type'),
  c('invoiceCreateBy', 'Purchase By'),
  c('totalAmount', 'Payment Amount', 'money'),
  c('taxAmount', 'Tax Amount', 'money'),
  c('transactionDate', 'Purchase Date', 'datetime'),
];
const uniqueColumns = (...groups: ReportColumn[][]): ReportColumn[] => {
  const seen = new Set<string>();
  return groups.flat().filter((x) => !seen.has(x.key) && seen.add(x.key));
};

/** One line per invoice, with its subscriber: the payment builders draw on it. */
const SUBSCRIBER_PAYMENTS_DEF: ReportDef = {
  key: 'subscriber-payments',
  group: 'screen',
  name: 'Subscriber Payments',
  heading: 'Subscriber Payments',
  code: 'PC429',
  filters: [{ type: 'agentChain' }, { type: 'dateRange', key: 'date', label: 'Date Range' }],
  columns: uniqueColumns(
    SUBSCRIBER_SHORT,
    CHAIN_COLUMNS,
    PAYMENT_INFO_COLUMNS,
    INVOICE_INFO_COLUMNS,
    PURCHASE_INFO_COLUMNS,
  ),
};

/** "Subscriber Notes" of Subscribers Note, less Date Closed and Employee Closed (notes are not closed here). */
const NOTE_COLUMNS: ReportColumn[] = [
  c('noteType', 'Note Type'),
  c('notes', 'Customer Notes'),
  c('priority', 'Note Priority'),
  c('noteCreated', 'Date Created', 'datetime'),
  c('employee', 'Employee'),
];
const SUBSCRIBER_NOTES_DEF: ReportDef = {
  key: 'subscriber-notes',
  group: 'screen',
  name: 'Subscribers Note',
  heading: 'Subscribers Note',
  code: 'PC429',
  filters: [
    { type: 'dateRange', key: 'date', label: 'Date Range' },
    { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
    { type: 'agentChain' },
    { type: 'text', key: 'noteType', label: 'Note Type', maxLength: 100 },
  ],
  columns: [...SUBSCRIBER_SHORT, ...NOTE_COLUMNS],
};

/** "Enrollment Activity Information" (the 29 of the reference less those not kept). */
const ENROLLMENT_DETAIL_DEF: ReportDef = {
  key: 'enrollment-detail',
  group: 'screen',
  name: 'Enrolment Activity Report',
  heading: 'Enrolment Activity Report',
  code: 'PC429',
  filters: [
    { type: 'agentChain' },
    { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
    {
      type: 'select',
      key: 'enrollmentStatus',
      label: 'Enrolment status',
      options: ['ALL', 'Activated', 'Rejected', 'Pending'],
    },
    { type: 'dateRange', key: 'date', label: 'Date Range' },
  ],
  columns: [
    c('enrollmentId', 'Enrollment ID'),
    c('masterAgent', 'Master Agent'),
    c('distributor', 'Distributor'),
    c('retailer', 'Retailer'),
    c('employee', 'Employee'),
    c('name', 'Name'),
    c('address', 'Address'),
    c('city', 'City'),
    c('state', 'State'),
    c('zip', 'ZIP'),
    c('email', 'Email'),
    c('sim', 'SIM'),
    c('mdn', 'MDN'),
    c('alternatePhone', 'Alternate Phone'),
    c('enrollmentType', 'Enrollment Type'),
    c('makeModel', 'Make Model'),
    c('planName', 'Plan Name'),
    c('handover', 'Handover'),
    c('enrollmentStatus', 'Status'),
    c('orderDate', 'Order Date', 'datetime'),
  ],
};

/** "SIM Information" of SIM Status. */
const SIM_STATUS_DEF: ReportDef = {
  key: 'sim-status',
  group: 'screen',
  name: 'SIM Status',
  heading: 'SIM Status',
  code: 'PC429',
  filters: [{ type: 'dateRange', key: 'date', label: 'Date Range' }, { type: 'agentChain' }],
  columns: [
    c('customerId', 'Customer ID'),
    c('accountStatus', 'Account Status'),
    c('mdn', 'MDN'),
    c('sim', 'SIM'),
    c('simStatus', 'SIM Current Status'),
    c('masterAgent', 'Master Agent'),
    c('distributor', 'Distributor'),
    c('retailer', 'Retailer'),
    c('employee', 'Employee'),
  ],
};

/** "Inventory Information" (INV) of Inventory History, less SIM DEC / HEX, MEID and BOX ID. */
const INVENTORY_HISTORY_DEF: ReportDef = {
  key: 'inventory-history',
  group: 'screen',
  name: 'Inventory History',
  heading: 'Inventory History',
  code: 'PC429',
  filters: [{ type: 'dateRange', key: 'date', label: 'Date Range' }],
  columns: [
    c('sim', 'SIM'),
    c('model', 'Model'),
    c('carrier', 'Carrier'),
    c('uploadedBy', 'Uploaded By'),
    c('inventoryStatus', 'Inventory Status'),
    c('masterAgent', 'Master Agent'),
    c('distributor', 'Distributor'),
    c('retailer', 'Retailer'),
    c('agent', 'Agent'),
    c('enrollId', 'Enroll ID'),
    c('deviceId', 'Device ID'),
    c('uploadedAt', 'Uploaded Date', 'datetime'),
  ],
};

/**
 * Product Purchase Log Report: the carrier calls of Add Product, one line per line and product
 * (the reference's parameters and its one column group). Attempt counts the calls for that
 * product on that line; Process Status and Carrier Response are those of the last one.
 */
const PRODUCT_PURCHASE_LOG_DEF: ReportDef = {
  key: 'product-purchase-log',
  group: 'screen',
  name: 'Product Purchase Log Report',
  heading: 'Product Purchase Log Report',
  code: 'PC429',
  feature: 'custom-product-purchase-log',
  filters: [
    { type: 'text', key: 'customerId', label: 'Customer ID', maxLength: 11 },
    { type: 'text', key: 'mdn', label: 'MDN', maxLength: 10 },
    { type: 'text', key: 'sim', label: 'SIM', maxLength: 25 },
    {
      type: 'select',
      key: 'status',
      label: 'Status',
      options: ['SUCCESS', 'FAIL'],
      emptyLabel: 'Select',
    },
    { type: 'dateRange', key: 'date', label: 'Date' },
  ],
  columns: [
    c('customerId', 'Customer ID'),
    c('mdn', 'MDN'),
    c('sim', 'SIM'),
    c('carrier', 'Carrier'),
    c('processStatus', 'Process Status'),
    c('attempt', 'Attempt', 'number'),
    c('lastAttemptAt', 'Last Attempt Date', 'datetime'),
    c('requestedAt', 'Request Date', 'datetime'),
    c('carrierResponse', 'Carrier Response'),
  ],
};

/** History type of Subscriber History, in the reference's order. */
export const SUBSCRIBER_HISTORY_TYPES = [
  'A/C Status History',
  'Address History',
  'CSR Search History',
  'SIM History',
  'MDN Status History',
  'Name History',
  'Plan History',
  'Resend History',
] as const;

/** "History Information" of Subscriber History. */
const HISTORY_COLUMNS: ReportColumn[] = [
  c('requestDate', 'Request Date', 'datetime'),
  c('historyStatus', 'Status'),
  c('updatedBy', 'Updated By'),
];

/**
 * Subscriber History: one line per entry of the chosen history of the subscribers. Assumption:
 * the subscriber columns are the short set the note and payment builders show, not all of S.
 */
const SUBSCRIBER_HISTORY_DEF: ReportDef = {
  key: 'subscriber-history',
  group: 'screen',
  name: 'Subscriber History',
  heading: 'Subscriber History',
  code: 'PC429',
  feature: 'custom-subscriber-history',
  filters: [
    { type: 'dateRange', key: 'date', label: 'Date' },
    { type: 'text', key: 'customerId', label: 'Customer ID', maxLength: 11 },
    {
      type: 'select',
      key: 'historyType',
      label: 'History Type',
      options: SUBSCRIBER_HISTORY_TYPES,
    },
  ],
  columns: [...SUBSCRIBER_SHORT, ...HISTORY_COLUMNS],
};

/** Release feature of the four builders whose files were read on 2026-10-24. */
const BUILDERS_2026_10_24 = 'custom-builders-2';

/**
 * Plan Changes Log Report: one line per plan change made on a line, with the columns of the
 * reference's file (walk of 2026-10-23, files of 2026-10-24). A renewal is not a change; a
 * dated change counts on the day it is made on the line. Assumptions: AGENT is the user who
 * made the change, and SERVICE LINE the line's MDN now.
 */
const PLAN_CHANGES_LOG_DEF: ReportDef = {
  key: 'plan-changes-log',
  group: 'screen',
  name: 'Plan Changes Log Report',
  heading: 'Plan Changes Log Report',
  code: 'PC429',
  feature: BUILDERS_2026_10_24,
  filters: [{ type: 'dateRange', key: 'date', label: 'Date Range' }],
  columns: [
    c('accountNo', 'ACCOUNT NUMBER'),
    c('serviceLine', 'SERVICE LINE'),
    c('agent', 'AGENT'),
    c('oldPlan', 'OLD PLAN'),
    c('newPlan', 'NEW PLAN'),
    c('changedAt', 'DATE CHANGE', 'datetime'),
  ],
};

/**
 * Simple Commission Report: one line per commission earned (the ledger of the commission
 * matrix). "Terminal ID(External Transaction ID)" is left out: no sale here has one.
 * Assumption: the reference's required multi-select of master agents is the agent chain,
 * which may be left empty.
 */
const SIMPLE_COMMISSION_DEF: ReportDef = {
  key: 'simple-commission',
  group: 'screen',
  name: 'Simple Commission Report',
  heading: 'Simple Commission Report',
  code: 'PC429',
  feature: BUILDERS_2026_10_24,
  filters: [{ type: 'dateRange', key: 'date', label: 'Date Range' }, { type: 'agentChain' }],
  columns: [
    c('activityType', 'Activity Type'),
    c('sim', 'SIM'),
    c('mdn', 'MDN'),
    c('enrollmentId', 'Enrollment ID'),
    c('month', 'Month', 'number'),
    c('amount', 'Amount', 'money'),
  ],
};

/**
 * Inventory: one line per SIM. The reference also filters on 36 provisioning and line states
 * ("Inventory Unit Status") and shows Vendor, MSL, Last Updated Source and Service Type; this
 * application keeps none of those, so they are left out. Final Status is the SIM's status.
 */
const INVENTORY_UNITS_DEF: ReportDef = {
  key: 'inventory-units',
  group: 'screen',
  name: 'Inventory',
  heading: 'Inventory',
  code: 'PC429',
  feature: BUILDERS_2026_10_24,
  filters: [
    { type: 'dateRange', key: 'date', label: 'Date Range' },
    { type: 'carrier', key: 'carrier', label: 'Carrier' },
  ],
  columns: [
    c('sim', 'SIM'),
    c('model', 'Model'),
    c('carrier', 'Carrier'),
    c('finalStatus', 'Final Status'),
    c('uploadedAt', 'Uploaded Date', 'datetime'),
  ],
};

/** "Payment Source" of the Tax report, in the reference's order. */
export const TAX_PAYMENT_SOURCES = [
  'API',
  'Agent',
  'Apply Payment',
  'AutoPay',
  'CGM',
  'CSR',
  'Enrollment Portal',
  'IVR',
  'Other',
  'PIN',
  'POS',
  'Tablet',
] as const;

/**
 * Tax report: one line per paid invoice, with its tax. Left out: TAXES BREAKUP, PROCESSING FEE
 * TAX, SURE TAX TRANSACTION ID and PHONE MODEL (one state rate is kept, the fee is not taxed,
 * there is no tax service and an invoice holds no device).
 */
const TAX_REPORT_DEF: ReportDef = {
  key: 'tax-report',
  group: 'screen',
  name: 'Tax report',
  heading: 'Tax report',
  code: 'PC429',
  feature: BUILDERS_2026_10_24,
  filters: [
    { type: 'dateRange', key: 'date', label: 'Date Range' },
    {
      type: 'select',
      key: 'paymentSource',
      label: 'Payment Source',
      options: TAX_PAYMENT_SOURCES,
      multiple: true,
    },
  ],
  columns: [
    c('invoiceNo', 'INVOICE NO'),
    c('customerId', 'CUSTOMER ID'),
    c('enrollmentId', 'Enroll ID'),
    c('city', 'CITY'),
    c('state', 'STATE'),
    c('zip', 'ZIP CODE'),
    c('amount', 'AMOUNT', 'money'),
    c('taxes', 'TAXES', 'money'),
    c('processingFee', 'PROCESSING FEE', 'money'),
    c('total', 'TOTAL AMOUNT', 'money'),
    c('paymentType', 'PAYMENT TYPE'),
    c('invoiceType', 'INVOICE TYPE'),
    c('invoiceDate', 'INVOICE DATE', 'datetime'),
  ],
};

/**
 * Subscriber & Inventory Weekly Report (switch custom-builders-3): the reference's two tables of
 * the current week, Sunday to Saturday, in one CSV (the reference makes an XLS). A line is either a
 * subscriber count (Item, Count) or a master agent's phones (the inventory columns). Assumptions:
 * "Transferred Subscribers" and "RMA" are not kept, so they are left out; the "as of" counts are
 * those of the day the report runs; phones are devices.
 */
const WEEKLY_DEF: ReportDef = {
  key: 'weekly-summary',
  group: 'screen',
  name: 'Subscriber & Inventory Weekly Report',
  heading: 'Subscriber & Inventory Weekly Report',
  code: 'PC429',
  feature: 'custom-builders-3',
  filters: [{ type: 'agentChain' }],
  columns: [
    c('section', 'Report'),
    c('item', 'Item / Master Agent'),
    c('count', 'Count', 'number'),
    c('inventoryBegin', 'Inventory Beginning of the Week', 'number'),
    c('shipped', 'Sale Orders shipped during the Week', 'number'),
    c('used', 'Phones used during the Week', 'number'),
    c('bad', 'Phones in Bad Status', 'number'),
    c('inventoryEnd', 'Inventory End of the Week', 'number'),
  ],
};

/** Report Type of Usage Details Report, as the reference names them. */
export const USAGE_REPORT_TYPES = [
  'Consolidated Usage',
  'Plan-wise Usage',
  'MDN-wise Usage',
  'Details MDN-wise Usage',
] as const;

/**
 * Usage Details Report (switch custom-builders-3): the usage the carrier reports for the lines of
 * the hierarchy in the period, asked line by line (at most 500 lines a run). One set of columns
 * serves the four Report Types; each fills the columns of its layout in the reference's file:
 * Consolidated by day and carrier, Plan-wise by plan, MDN-wise by line, Details MDN-wise by line
 * and day. Voice in minutes, data in MB. Empty until a carrier that reports usage is connected.
 */
const USAGE_DETAILS_DEF: ReportDef = {
  key: 'usage-details',
  group: 'screen',
  name: 'Usage Details Report',
  heading: 'Usage Details Report',
  code: 'PC429',
  feature: 'custom-builders-3',
  filters: [
    {
      type: 'select',
      key: 'reportType',
      label: 'Report Type',
      options: USAGE_REPORT_TYPES,
      required: true,
    },
    { type: 'dateRange', key: 'date', label: 'Date' },
    { type: 'carrier', key: 'carrier', label: 'Carrier' },
    { type: 'agentChain' },
  ],
  columns: [
    c('usageDate', 'USAGE DATE', 'date'),
    c('mdn', 'MDN'),
    c('customerId', 'CUSTOMER ID'),
    c('planId', 'PLAN ID'),
    c('planName', 'PLAN NAME'),
    c('planCode', 'PLAN CODE'),
    c('lastRenewal', 'LAST RENEWAL DATE', 'date'),
    c('billCycle', 'BILL CYCLE DATE', 'date'),
    c('carrier', 'CARRIER'),
    c('voice', 'VOICE(MIN)', 'number'),
    c('sms', 'SMS', 'number'),
    c('data', 'DATA(MB)', 'number'),
    c('fromDate', 'FROM DATE', 'date'),
    c('toDate', 'TO DATE', 'date'),
    c('reportType', 'REPORT TYPE'),
    c('masterAgent', 'MASTER AGENT'),
    c('activationDate', 'ACTIVATION DATE', 'date'),
  ],
};

export const HIGH_USAGE_THRESHOLDS = [
  '>=500',
  '>=1000',
  '>=2000',
  '>=3000',
  '>=4000',
  '>=5000',
  '>=10000',
] as const;

/**
 * High Usage Report (switch custom-builders-3): lines whose usage in the period reached the value
 * chosen, for the type chosen (minutes, texts or MB). Assumptions: the reference showed no rows, so
 * its columns are 212Ez's; the period defaults to the last 30 days.
 */
const HIGH_USAGE_DEF: ReportDef = {
  key: 'high-usage',
  group: 'screen',
  name: 'High Usage Report',
  heading: 'High Usage Report',
  code: 'PC429',
  feature: 'custom-builders-3',
  filters: [
    {
      type: 'select',
      key: 'reportType',
      label: 'Report Type',
      options: ['VOICE', 'TEXT', 'DATA'],
      emptyLabel: 'All',
    },
    {
      type: 'select',
      key: 'value',
      label: 'Value',
      options: HIGH_USAGE_THRESHOLDS,
      emptyLabel: 'All',
    },
    { type: 'carrier', key: 'carrier', label: 'Carrier' },
    { type: 'dateRange', key: 'date', label: 'Date' },
    { type: 'agentChain' },
  ],
  columns: [
    c('mdn', 'MDN'),
    c('customerId', 'CUSTOMER ID'),
    c('planName', 'PLAN NAME'),
    c('carrier', 'CARRIER'),
    c('voice', 'VOICE(MIN)', 'number'),
    c('sms', 'SMS', 'number'),
    c('data', 'DATA(MB)', 'number'),
    c('masterAgent', 'MASTER AGENT'),
  ],
};

registerReportDefs([
  USAGE_DETAILS_DEF,
  HIGH_USAGE_DEF,
  WEEKLY_DEF,
  PLAN_CHANGES_LOG_DEF,
  SIMPLE_COMMISSION_DEF,
  INVENTORY_UNITS_DEF,
  TAX_REPORT_DEF,
  PRODUCT_PURCHASE_LOG_DEF,
  SUBSCRIBER_HISTORY_DEF,
  SUBSCRIBER_INFORMATION_DEF,
  SUBSCRIBER_STATUS_DEF,
  SUBSCRIBER_PAYMENTS_DEF,
  SUBSCRIBER_NOTES_DEF,
  ENROLLMENT_DETAIL_DEF,
  SIM_STATUS_DEF,
  INVENTORY_HISTORY_DEF,
]);

const keys = (cols: ReportColumn[]) => cols.map((x) => x.key);
const S = keys(SUBSCRIBER_COLUMNS);
const S_SHORT = keys(SUBSCRIBER_SHORT);

export const CUSTOM_REPORTS: readonly CustomReport[] = [
  /* ----------------------------- Subscriber Reports ---------------------------- */
  { key: 'agent-sales', group: 'subscriber', name: 'Agent Sales Report', source: 'agent-sales' },
  {
    key: 'billing-invoices',
    group: 'subscriber',
    name: 'Billing Invoices',
    source: 'subscriber-payments',
    sections: [
      { title: 'Subscriber Information', keys: [...S_SHORT, ...keys(CHAIN_COLUMNS)] },
      { title: 'Invoice Information', keys: keys(INVOICE_INFO_COLUMNS) },
    ],
  },
  {
    key: 'enrolment-activity',
    group: 'subscriber',
    name: 'Enrolment Activity Report',
    source: 'enrollment-detail',
    sections: [
      {
        title: 'Enrollment Activity Information',
        keys: keys([...ENROLLMENT_DETAIL_DEF.columns]),
      },
    ],
  },
  {
    key: 'handover-activity',
    group: 'subscriber',
    name: 'Handover Activity Report',
    source: 'handover-activity',
  },
  {
    key: 'high-usage',
    group: 'subscriber',
    name: 'High Usage Report',
    source: 'high-usage',
    feature: 'custom-builders-3',
  },
  {
    key: 'paid-equipment',
    group: 'subscriber',
    name: 'Paid Equipment Report',
    source: 'paid-equipment',
  },
  {
    key: 'payment-refunds',
    group: 'subscriber',
    name: 'Payment Refunds Report',
    source: 'payment-refunds',
    feature: 'payment-refunds',
  },
  {
    key: 'plan-changes-log',
    group: 'subscriber',
    name: 'Plan Changes Log Report',
    source: 'plan-changes-log',
    feature: BUILDERS_2026_10_24,
  },
  {
    key: 'prepaid-renewed',
    group: 'subscriber',
    name: 'Prepaid renewed report',
    source: 'prepaid-renewed',
  },
  {
    key: 'product-purchase-log',
    group: 'subscriber',
    name: 'Product Purchase Log Report',
    source: 'product-purchase-log',
    feature: 'custom-product-purchase-log',
    sections: [
      {
        title: 'Product Purchase Log Information',
        keys: keys([...PRODUCT_PURCHASE_LOG_DEF.columns]),
      },
    ],
  },
  {
    key: 'subscriber-activation',
    group: 'subscriber',
    name: 'Subscriber Activation Report',
    source: 'activation',
  },
  {
    key: 'subscriber-contacts',
    group: 'subscriber',
    name: 'Subscriber Contacts',
    source: 'subscriber-information',
    sections: [
      { title: 'Subscriber Information', keys: S },
      { title: 'Agent Information', keys: keys(AGENT_INFO_COLUMNS) },
    ],
  },
  {
    key: 'subscriber-history',
    group: 'subscriber',
    name: 'Subscriber History',
    source: 'subscriber-history',
    feature: 'custom-subscriber-history',
    sections: [
      { title: 'Subscriber Information', keys: S_SHORT },
      { title: 'History Information', keys: keys(HISTORY_COLUMNS) },
    ],
  },
  {
    key: 'subscriber-information',
    group: 'subscriber',
    name: 'Subscriber Information',
    source: 'subscriber-information',
    sections: [
      { title: 'Output Parameter', keys: S },
      { title: 'Inventory Information', keys: keys(SUBSCRIBER_INVENTORY_COLUMNS) },
    ],
  },
  {
    key: 'weekly-summary',
    group: 'subscriber',
    name: 'Subscriber & Inventory Weekly Report',
    source: 'weekly-summary',
    feature: 'custom-builders-3',
  },
  {
    key: 'subscribers-note',
    group: 'subscriber',
    name: 'Subscribers Note',
    source: 'subscriber-notes',
    sections: [
      { title: 'Subscriber Information', keys: S_SHORT },
      { title: 'Subscriber Notes', keys: keys(NOTE_COLUMNS) },
    ],
  },
  {
    key: 'subscriber-order',
    group: 'subscriber',
    name: 'Subscriber Order',
    source: 'subscriber-information',
    sections: [
      { title: 'Subscriber Information', keys: S },
      { title: 'Agent Information', keys: keys(AGENT_INFO_COLUMNS) },
      { title: 'MDN Information', keys: keys(MDN_COLUMNS) },
    ],
  },
  {
    key: 'subscriber-payments',
    group: 'subscriber',
    name: 'Subscriber Payments',
    source: 'subscriber-payments',
    sections: [
      { title: 'Subscriber Information', keys: [...S_SHORT, ...keys(CHAIN_COLUMNS)] },
      { title: 'Payment Information', keys: keys(PAYMENT_INFO_COLUMNS) },
    ],
  },
  {
    key: 'subscriber-purchases',
    group: 'subscriber',
    name: 'Subscriber Purchases Detail',
    source: 'subscriber-payments',
    sections: [
      { title: 'Subscriber Information', keys: S_SHORT },
      { title: 'Purchase Information', keys: keys(PURCHASE_INFO_COLUMNS) },
    ],
  },
  {
    key: 'subscriber-status',
    group: 'subscriber',
    name: 'Subscriber Status Report',
    source: 'subscriber-status',
    sections: [
      { title: 'Subscriber Information', keys: S },
      { title: 'Other Information', keys: keys(OTHER_COLUMNS) },
    ],
  },
  {
    key: 'subscriber-telephone',
    group: 'subscriber',
    name: 'Subscriber Telephone',
    source: 'subscriber-information',
    sections: [
      { title: 'Subscriber Information', keys: S.filter((k) => k !== 'status') },
      { title: 'Agent Information', keys: keys(AGENT_INFO_COLUMNS) },
      { title: 'Inventory Information', keys: keys(SUBSCRIBER_INVENTORY_COLUMNS) },
    ],
  },
  {
    key: 'usage-details',
    group: 'subscriber',
    name: 'Usage Details Report',
    source: 'usage-details',
    feature: 'custom-builders-3',
  },
  {
    key: 'all-commission-matrix',
    group: 'subscriber',
    name: 'All Commission Matrix',
    source: 'all-commission-matrix',
  },
  {
    key: 'simple-commission',
    group: 'subscriber',
    name: 'Simple Commission Report',
    source: 'simple-commission',
    feature: BUILDERS_2026_10_24,
  },

  /* ------------------------------- Subscriber Lists ---------------------------- */
  {
    key: 'subscriber-list-general',
    group: 'lists',
    name: 'Subscriber List General',
    source: 'subscriber-information',
    sections: [
      {
        title: 'Subscriber Information',
        keys: S.filter((k) => k !== 'activatedAt'),
      },
      {
        title: 'Subscriber Contacts',
        keys: [
          'firstName',
          'lastName',
          'address1',
          'address2',
          'city',
          'state',
          'zip',
          'contactPhone',
          'email',
        ],
      },
      {
        title: 'Subscriber Billing Information',
        keys: ['billingAddress1', 'billingAddress2', 'billingCity', 'billingState', 'billingZip'],
      },
    ],
  },

  /* ------------------------------ Inventory Reports ---------------------------- */
  {
    key: 'inventory',
    group: 'inventory',
    name: 'Inventory',
    source: 'inventory-units',
    feature: BUILDERS_2026_10_24,
    sections: [{ title: 'Inventory', keys: keys([...INVENTORY_UNITS_DEF.columns]) }],
  },
  {
    key: 'inventory-history',
    group: 'inventory',
    name: 'Inventory History',
    source: 'inventory-history',
    sections: [{ title: 'Inventory Information', keys: keys([...INVENTORY_HISTORY_DEF.columns]) }],
  },
  {
    key: 'sim-status',
    group: 'inventory',
    name: 'SIM Status',
    source: 'sim-status',
    sections: [{ title: 'SIM Information', keys: keys([...SIM_STATUS_DEF.columns]) }],
  },

  /* ------------------------------- Billing Reports ----------------------------- */
  {
    key: 'enhance-payment',
    group: 'billing',
    name: 'Enhance Payment Report',
    source: 'enhance-payment',
  },
  {
    key: 'invoices',
    group: 'billing',
    name: 'Invoices',
    source: 'subscriber-payments',
    sections: [
      { title: 'Subscriber Information', keys: [...S_SHORT, ...keys(CHAIN_COLUMNS)] },
      { title: 'Invoice Information', keys: keys(INVOICE_INFO_COLUMNS) },
    ],
  },
  { key: 'revenue', group: 'billing', name: 'Revenue report', source: 'revenue' },
  {
    key: 'tax-report',
    group: 'billing',
    name: 'Tax report',
    source: 'tax-report',
    feature: BUILDERS_2026_10_24,
  },
  { key: 'autopay', group: 'billing', name: 'Autopay Report', source: 'autopay' },
  {
    key: 'wallet-balance',
    group: 'billing',
    name: 'Wallet balance report',
    source: 'wallet-balance',
  },
];

export const getCustomReport = (key: string): CustomReport | undefined =>
  CUSTOM_REPORTS.find((r) => r.key === key);

/** The definition a builder draws on. */
export const customReportSource = (report: CustomReport): ReportDef => getReportDef(report.source)!;

/** Permission codes a user needs to run a builder: PC429 and, for a borrowed report, its own. */
export const customReportCodes = (report: CustomReport): string[] => [
  ...new Set(['PC429', customReportSource(report).code]),
];

/** Column groups of a builder, resolved to columns. */
export function customReportSections(
  report: CustomReport,
): Array<{ title: string; columns: ReportColumn[] }> {
  const def = customReportSource(report);
  if (!report.sections) return [{ title: 'Output Parameter', columns: [...def.columns] }];
  const byKey = new Map(def.columns.map((x) => [x.key, x]));
  return report.sections.map((s) => ({
    title: s.title,
    columns: s.keys.map((k) => byKey.get(k)!).filter(Boolean),
  }));
}

/* ----------------------------- Saved parameters ---------------------------- */

export const SAVED_VIEW_TYPES = ['Public', 'Private'] as const;

/** "Schedule date" of the Saved Parameters dialog: the dates a saved report covers when it runs. */
export const SAVED_DATE_PRESETS = [
  'Today',
  'Yesterday',
  'Last 7 Days',
  'Last 14 Days',
  'This Month',
  'Last 30 Days',
  'Last Month',
  'Last 60 Days',
  'Last 90 Days',
] as const;
export type SavedDatePreset = (typeof SAVED_DATE_PRESETS)[number];

const day = (d: Date): string => d.toISOString().slice(0, 10);

/** The first and last day (UTC, YYYY-MM-DD) a preset stands for on `now`. */
export function presetRange(preset: SavedDatePreset, now: Date = new Date()): [string, string] {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const back = (days: number) => new Date(today.getTime() - days * 86_400_000);
  switch (preset) {
    case 'Today':
      return [day(today), day(today)];
    case 'Yesterday':
      return [day(back(1)), day(back(1))];
    case 'This Month':
      return [day(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))), day(today)];
    case 'Last Month':
      return [
        day(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1))),
        day(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0))),
      ];
    default: {
      // "Last N Days" ends today and includes it.
      const days = Number(preset.split(' ')[1]);
      return [day(back(days - 1)), day(today)];
    }
  }
}

const columnKeys = z
  .array(z.string().trim().min(1).max(60))
  .min(1, 'Select at least one output column')
  .max(120);

/** "Save My Selection": Save as, View type, Schedule date, with the builder's selection. */
export const savedReportSchema = z.object({
  reportKey: z.string().trim().min(1).max(60),
  name: z.string().trim().min(1, 'Save as is required').max(100),
  viewType: z.enum(SAVED_VIEW_TYPES, { required_error: 'Select View type' }),
  datePreset: z.enum(SAVED_DATE_PRESETS, { required_error: 'Select Schedule date' }),
  columns: columnKeys,
  /** The builder's search parameters other than its date range, as the report page sends them. */
  filters: z.record(z.string().max(120)).default({}),
});
export type SavedReportInput = z.input<typeof savedReportSchema>;

export const savedReportListQuerySchema = z.object({
  name: z.string().trim().max(100).optional(),
  reportKey: z.string().trim().max(60).optional(),
});

export interface SavedReportDto {
  id: string;
  name: string;
  reportKey: string;
  reportName: string;
  viewType: (typeof SAVED_VIEW_TYPES)[number];
  datePreset: SavedDatePreset;
  columns: string[];
  createdBy: UserRef | null;
  createdAt: string;
  /** The caller saved it, or is an administrator: may delete it. */
  mine: boolean;
}

/* ------------------------------ Scheduled tasks ---------------------------- */

/**
 * How often a scheduled report is produced. Assumption: the reference's "Schedule Task"
 * dialog showed only its Report Name field, so the frequencies are this application's own.
 */
export const SCHEDULE_FREQUENCIES = ['Daily', 'Weekly', 'Monthly'] as const;
export type ScheduleFrequency = (typeof SCHEDULE_FREQUENCIES)[number];

export const scheduleTaskSchema = z.object({
  savedReportId: objectId,
  name: z.string().trim().min(1, 'Report Name is required').max(100),
  frequency: z.enum(SCHEDULE_FREQUENCIES, { required_error: 'Select Frequency' }),
});
export type ScheduleTaskInput = z.input<typeof scheduleTaskSchema>;

export interface ScheduledRunDto {
  id: string;
  generatedAt: string;
  status: 'Success' | 'Failed';
  rows: number;
  message: string;
}

export interface ScheduledTaskDto {
  id: string;
  name: string;
  savedReportName: string;
  reportName: string;
  frequency: ScheduleFrequency;
  nextRunAt: string;
  lastRunAt: string | null;
  createdBy: UserRef | null;
  createdAt: string;
  /** Latest runs, newest first. */
  runs: ScheduledRunDto[];
  mine: boolean;
}
